<template>
  <div class="body2 mr-1">{{ $t("general.workspace") }}:</div>
  <v-menu v-if="enableDropdown" v-model="menu" :close-on-content-click="false" location="bottom">
    <template v-slot:activator="{ props }">
      <div v-bind="props" role="button" class="d-flex align-center">
        <span class="font-weight-bold">
          {{ currentWorkspace.name }}
        </span>
        <v-icon icon="$triangle"></v-icon>
      </div>
    </template>

    <v-card min-width="400" elevation="2" max-height="450">
      <div class="bg-white pa-4 position-sticky top-0 z-index-9999">
        <v-text-field
          density="comfortable"
          rounded
          min-width="400"
          clear-icon="$x"
          :placeholder="$t('general.search')"
          :aria-label="$t('general.search')"
          prepend-inner-icon="$search"
          clearable
          variant="filled"
          bg-color="indigo-faint"
          :hide-details="true"
          @update:model-value="workspaceSearch = $event"
        >
        </v-text-field>
      </div>

      <v-list>
        <v-list-item density="comfortable">
          <template v-slot:title>
            <div class="caption">
              <span v-text="$t('general.workspaces', { n: total }, total)"></span>
            </div>
          </template>
        </v-list-item>
        <v-list-item
          v-for="(workspace, i) in workspaces"
          :id="i.toString()"
          density="comfortable"
          variant="flat"
          height="56"
          :key="workspace.workspaceId"
          :active="workspace.workspaceId === currentWorkspace.workspaceId"
          @click="selectWorkplace(workspace)"
          v-intersect="onIntersect"
        >
          <template v-slot:title>
            <div class="d-flex align-center">
              <v-icon class="mr-2" size="40" :icon="iconsMap[workspace.type]"></v-icon>
              <span v-text="workspace.name"></span>
            </div>
          </template>
        </v-list-item>
        <div v-if="loading" class="d-flex align-center justify-center">
          <v-progress-circular size="40" indeterminate></v-progress-circular>
        </div>
      </v-list>
    </v-card>
  </v-menu>
  <div v-else class="font-weight-bold">
    {{ currentWorkspace.name }}
  </div>
</template>

<script lang="ts">
import { WorkspaceAction, WorkspaceType } from "@/constants/workplaces";
import { useWorkspacesListStore, type WorkspaceListItem } from "@/_store/workspaces-list.module.ts";
import { storeToRefs } from "pinia";
import { computed, onUnmounted, ref, watch } from "vue";
import { RouteName } from "@/constants/routes";
import { useRouter } from "vue-router";
import { trackGtmEvent, useResetStore } from "@/_helpers/utils.ts";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import { useAccountStore } from "@/_store/account.module.ts";

export default {
  props: {
    enableDropdown: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  setup() {
    const workspacesStore = useWorkspacesListStore();
    const workspaceSearch = ref("");
    const router = useRouter();
    let workspaceTimer: ReturnType<typeof setTimeout>;
    const { setPagination, getWorkspaces, resetState, setWorkspaces } = workspacesStore;
    const { total, workspaces, pagination, loading } = storeToRefs(workspacesStore);
    const menu = ref(false);
    const workspaceStore = useWorkspaceStore();
    const currentWorkspace = computed(() => ({
      name: workspaceStore.workspace.name,
      workspaceId: workspaceStore.workspace.workspaceId,
    }));

    const selectWorkplace = async (workplace: WorkspaceListItem) => {
      // Resettings all stores except ignore list. Reset should be 1st as order is important
      useResetStore().allExceptIgnoreList();
      // Getting new workspace details and permissions
      await workspaceStore.updateWorkspaceAndPermissions(workplace.workspaceId, false);
      await useAccountStore().checkMspAccess();
      await router.push({ name: RouteName.DASHBOARD_PAGE });
      menu.value = false;
      trackGtmEvent(GtmAction.WORKSPACE_SELECTOR, GTMParams.WORKSPACE_SELECTED);
    };

    const onIntersect = async (isIntersecting: boolean, entries: IntersectionObserverEntry[]) => {
      const intersectedItemId = Number(entries[0].target.id);
      if (isIntersecting && intersectedItemId === (workspaces.value ?? []).length - 1) {
        // last tab reached in backend, don't do new request
        if (intersectedItemId === total.value - 1) {
          return;
        }
        loadMoreWorkplaces();
      }
    };

    const loadMoreWorkplaces = () => {
      setPagination({
        page: pagination.value.page + 1,
        pageSize: 25,
      });
      getWorkspaces(workspaceSearch.value, true);
    };

    const updateSearch = (value: string, skipRequest = false) => {
      workspaceSearch.value = value;

      if (skipRequest) return;

      // cancel pending call
      clearTimeout(workspaceTimer);
      // delay new call 500ms
      workspaceTimer = setTimeout(() => {
        setWorkspaces([]);
        setPagination({
          page: 0,
          pageSize: 25,
        });
        getWorkspaces(value ?? "");
      }, 500);
    };

    watch(workspaceSearch, (newVal) => {
      updateSearch(newVal);
    });

    watch(menu, (opened) => {
      if (opened) {
        setPagination({
          page: 0,
          pageSize: 25,
        });
        getWorkspaces();
        trackGtmEvent(GtmAction.WORKSPACE_SELECTOR, GTMParams.SELECTOR_OPENED);
      } else {
        // Clear search and skip request
        updateSearch("", true);
      }
    });

    onUnmounted(() => {
      resetState();
    });

    return {
      menu,
      loading,
      workspaceSearch,
      workplacesActions: WorkspaceAction,
      workspaces,
      total,
      pagination,
      selectWorkplace,
      onIntersect,
      currentWorkspace,
      iconsMap: {
        [WorkspaceType.REGULAR]: "icon-regular-plan-s",
        [WorkspaceType.CHANNEL]: "icon-parent-plan-s",
        [WorkspaceType.CHILD]: "icon-child-plan-s",
      },
    };
  },
};
</script>

<style lang="scss" scoped>
:deep(*) {
  .v-field__outline {
    display: none !important;
  }
}
</style>
